/**
 * Which sub-tabs of the Admin page a viewer gets.
 *
 * Pure and in its own module so it can be tested directly: importing
 * `AdminPage.tsx` from a spec drags in every admin panel and the API client.
 *
 * ⚠️ THIS IS PRESENTATION, NOT THE GATE. Each API route enforces its own
 * permission: user, role, division and owned-equipment management need
 * `admin.access`, the rate-sheet publish needs `equipment_rates.manage`, and the
 * three feedback routes accept `feedback.manage` as well as `admin.access`.
 * Filtering the strip stops a Feedback Admin being shown screens that would only
 * 403; it is the endpoints that keep them out of user and role management.
 */

export type AdminTab =
  | 'users' | 'roles' | 'divisions' | 'config' | 'integrations'
  | 'owned-equipment' | 'equipment-rates' | 'feedback' | 'training' | 'job-addresses' | 'roll-ups';

export const TAB_LABELS: { key: AdminTab; label: string }[] = [
  { key: 'users',           label: 'Users & Roles'   },
  { key: 'roles',           label: 'Roles'           },
  { key: 'divisions',       label: 'Divisions'       },
  { key: 'owned-equipment', label: 'Owned Equipment' },
  { key: 'equipment-rates', label: 'Equipment Rates' },
  { key: 'feedback',        label: 'Feedback'        },
  { key: 'training',        label: 'Training'        },
  { key: 'config',          label: 'Config'          },
  { key: 'integrations',    label: 'Integrations'    },
  { key: 'job-addresses',   label: 'Job Addresses'   },
  { key: 'roll-ups',        label: 'Job Roll-ups'    },
];

/**
 * Admin gets every tab. Anyone else who reached the page (the Feedback Admin
 * role) gets Feedback alone — and it is the FIRST tab, so the page opens on it.
 *
 * Default-deny on purpose: a tab added to `TAB_LABELS` later is Admin-only until
 * somebody decides otherwise here, rather than appearing for the Feedback Admin
 * by omission.
 */
export const NON_ADMIN_TABS: ReadonlySet<AdminTab> = new Set<AdminTab>(['feedback']);

export function visibleAdminTabs(isAdmin: boolean): { key: AdminTab; label: string }[] {
  return isAdmin ? TAB_LABELS : TAB_LABELS.filter((t) => NON_ADMIN_TABS.has(t.key));
}
